import { Color } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import {
  MatchIconAlert,
  MatchIconPositive,
  MatchIconWarning,
} from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import TableLayout, { TableIconTD, TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { ReportChainalysisAddressScreening } from 'dashboard/models/report/integration/chainalysis-address-screening';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker, { StickerProps } from 'design-system/components/Sticker';

const RiskAssessmentTH = styled(TableTH)`
  width: 320px;
`;

const InfoPlaceholder = styled('div')`
  color: ${Color.CoreSlate700};
`;

const redactedSticker = <Sticker>Redacted</Sticker>;

const riskLevelIcon = (riskLevel?: string) => {
  if (!riskLevel) {
    return <></>;
  }

  switch (riskLevel.toLowerCase()) {
    case 'severe':
    case 'high':
      return <MatchIconAlert />;
    case 'medium':
      return <MatchIconWarning />;
    case 'low':
      return <MatchIconPositive />;
  }
};

const riskLevelSticker = (riskLevel?: string) => {
  let variant: StickerProps['variant'] = 'basic';

  switch (riskLevel?.toLowerCase()) {
    case 'severe':
    case 'high':
      variant = 'danger';
      break;
    case 'medium':
      variant = 'caution';
      break;
    case 'low':
      variant = 'success';
      break;
  }

  return <Sticker variant={variant}>{riskLevel}</Sticker>;
};

interface ChainalysisAddressScreeningProps {
  report: ReportChainalysisAddressScreening;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const ChainalysisAddressScreening: VFC<ChainalysisAddressScreeningProps> = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: ChainalysisAddressScreeningProps) => {
  const riskLevel = report.attributes.result?.risk;
  const riskReason = report.attributes.result?.riskReason;

  const riskAssessmentContent = (
    <>
      <Card.Title>Risk Assessment</Card.Title>
      <TableLayout
        dataTest='risk-assessment-table'
        isLoading={false}
        showHeader={true}
        isEmpty={false}
        nullState={null}
        headerTR={
          <>
            <TableTH data-test='risk-assessment-table__icon-header' />
            <RiskAssessmentTH data-test='risk-assessment-table__risk-property-header'>
              Risk Property
            </RiskAssessmentTH>
            <TableTH data-test='risk-assessment-table__value-header'>Value</TableTH>
          </>
        }
      >
        <TableTR
          data-test='risk-assessment-table__row--risk-level'
          id='risk-assessment-table__row--risk-level'
          key='risk-level'
        >
          <TableIconTD>{riskLevelIcon(riskLevel)}</TableIconTD>
          <TableTD>
            <span>Risk level</span>
          </TableTD>
          <TableTD>{isRedacted ? redactedSticker : riskLevelSticker(riskLevel)}</TableTD>
        </TableTR>
        <TableTR
          data-test='risk-assessment-table__row--risk-reason'
          id='risk-assessment-table__row--risk-reason'
          key='risk-reason'
        >
          <TableIconTD>{riskLevelIcon(riskLevel)}</TableIconTD>
          <TableTD>
            <span>Risk reason</span>
          </TableTD>
          <TableTD>
            {isRedacted ? (
              redactedSticker
            ) : riskReason ? (
              riskReason
            ) : (
              <InfoPlaceholder>N/A</InfoPlaceholder>
            )}
          </TableTD>
        </TableTR>
      </TableLayout>
    </>
  );

  const clusterContent = (
    <>
      <Card.Title>Cluster</Card.Title>
      <AttributeGrid colCount={2}>
        <AttributeItem
          title='Name'
          value={report.attributes.result?.cluster?.name}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-chainalysis-address-screening_cluster-name'
        />
        <AttributeItem
          title='Category'
          value={report.attributes.result?.cluster?.category}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-chainalysis-address-screening_cluster-category'
        />
      </AttributeGrid>
    </>
  );

  const exposuresColCount = report.attributes.result?.exposures
    ? Math.min(report.attributes.result?.exposures?.length, 3)
    : 3;
  const exposuresContent = (
    <>
      <Card.Title>Exposures</Card.Title>
      <AttributeGrid colCount={exposuresColCount}>
        {report.attributes.result?.exposures?.map((exposure) => (
          <AttributeItem
            key={exposure.category}
            title={exposure.category}
            value={exposure.value ? '$' + Math.round(exposure.value * 100) / 100 : undefined}
            placeholder='N/A'
            isRedacted={isRedacted}
            dataTest={`report-chainalysis-address-screening_exposure-${exposure.category}`}
          />
        ))}
      </AttributeGrid>
    </>
  );

  const addressIdentifications = report.attributes.result?.addressIdentifications;

  const addressIdentificationsContent = (
    <>
      <Card.Title>Address Identifications</Card.Title>
      {addressIdentifications && addressIdentifications.length > 0 ? (
        addressIdentifications.map((addressIdentification, idx) => (
          <Accordion key={addressIdentification.name} title={addressIdentification.name}>
            <Spacer height={2} />
            <AttributeGrid key={idx}>
              <AttributeItem
                title='Category'
                value={addressIdentification.category}
                placeholder='N/A'
                isRedacted={isRedacted}
                dataTest={`report-chainalysis-address-screening_address-identification-category-${idx}`}
              />
              <AttributeItem
                title='Description'
                value={addressIdentification.description}
                placeholder='N/A'
                isRedacted={isRedacted}
                dataTest={`report-chainalysis-address-screening_address-identification-description-${idx}`}
              />
            </AttributeGrid>
          </Accordion>
        ))
      ) : (
        <InfoPlaceholder>N/A</InfoPlaceholder>
      )}
    </>
  );
  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      {riskAssessmentContent}
      {clusterContent}
      {exposuresContent}
      {addressIdentificationsContent}
    </ReportResultLayout>
  );
};

export default ChainalysisAddressScreening;
